package ai.jessie.web.invoices

import ai.jessie.shared.invoices.InvoiceProgressResponse
import ai.jessie.shared.invoices.InvoiceResponse
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.CardBodyStyle
import ai.jessie.web.theme.CardStyle
import ai.jessie.web.theme.FieldInputStyle
import ai.jessie.web.theme.FieldsetStyle
import ai.jessie.web.theme.LegendStyle
import ai.jessie.web.theme.RowStyle
import ai.jessie.web.theme.SmallStyle
import ai.jessie.web.theme.TextAreaStyle
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Fieldset
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Legend
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea
import org.w3c.dom.HTMLElement

// Step 7 with A1, A2, A4 and A5: the consequences and required inputs; Enter confirms.
@Composable
internal fun transitionConfirmation(
    progress: InvoiceProgressResponse,
    target: String,
    state: InvoiceProgressUiState,
    actions: InvoiceProgressActions,
) {
    // An in-card confirmation box, as the project change confirmation.
    Form(
        attrs =
            CardStyle.toAttrs {
                id(CONFIRMATION_ID)
                attr("role", "alertdialog")
                attr("aria-labelledby", CONFIRMATION_TITLE_ID)
                attr("aria-describedby", CONFIRMATION_TEXT_ID)
                attr("tabindex", "-1")
                addEventListener("submit") {
                    it.preventDefault()
                    actions.confirm()
                }
            }
    ) {
        Fieldset(attrs = FieldsetStyle.toAttrs { if (state.busy) attr("disabled", "") }) {
            Legend(attrs = LegendStyle.toAttrs { id(CONFIRMATION_TITLE_ID) }) {
                Text(transitionLabel(target))
            }
            Div(attrs = CardBodyStyle.toAttrs()) {
                P(attrs = SmallStyle.toAttrs { id(CONFIRMATION_TEXT_ID) }) {
                    Text(consequences(target, progress.invoice))
                }
                when (target) {
                    SENT -> dateInput("Versanddatum", progress, state, actions)
                    PAID -> dateInput("Zahlungsdatum", progress, state, actions)
                    CANCELLED -> reasonInput(state, actions)
                }
                // The confirmation comes first, as before; Tab order is part of the flow.
                Div(attrs = RowStyle.toAttrs()) {
                    themedButton(
                        confirmLabel(target),
                        {},
                        variant = transitionVariant(target),
                        submit = true,
                    )
                    themedButton("Abbrechen", actions.cancel, disabled = state.busy)
                }
            }
        }
    }
    LaunchedEffect(target) {
        if (!AppGlobals.isExporting)
            (document.getElementById(CONFIRMATION_ID) as? HTMLElement)?.focus()
    }
}

// A2, A4 and A6: the proposal is today in the organisation's time zone; the server checks the
// range.
@Composable
private fun dateInput(
    label: String,
    progress: InvoiceProgressResponse,
    state: InvoiceProgressUiState,
    actions: InvoiceProgressActions,
) {
    val field =
        InvoiceField(
            "date",
            "$label (Pflichtfeld, zwischen ${progress.invoice.issueDate} und " +
                "${progress.currentDate})",
            state.date,
            state.problems["date"],
        )
    invoiceFieldBlock(field) {
        Input(
            InputType.Date,
            attrs =
                FieldInputStyle.toAttrs {
                    value(field.value)
                    attr("min", progress.invoice.issueDate)
                    attr("max", progress.currentDate)
                    progressFieldAttributes(field)
                    onInput { actions.changeDate(it.value) }
                },
        )
    }
}

// A5.
@Composable
private fun reasonInput(state: InvoiceProgressUiState, actions: InvoiceProgressActions) {
    val field =
        InvoiceField(
            "cancellationReason",
            "Stornierungsgrund (Pflichtfeld, höchstens $MAX_REASON Zeichen)",
            state.reason,
            state.problems["cancellationReason"],
        )
    invoiceFieldBlock(field) {
        TextArea(
            value = field.value,
            attrs =
                TextAreaStyle.toAttrs {
                    attr("rows", "3")
                    attr("maxlength", "$MAX_REASON")
                    progressFieldAttributes(field)
                    onInput { actions.changeReason(it.value) }
                },
        )
    }
}

// A rejected input refers to the shown problem.
private fun AttrsScope<*>.progressFieldAttributes(field: InvoiceField) {
    id(field.id)
    attr("name", field.name)
    attr("required", "")
    attr("autocomplete", "off")
    if (field.problem != null) {
        attr("aria-invalid", "true")
        attr("aria-describedby", PROGRESS_ERROR_ID)
    }
}

internal fun transitionLabel(target: String) =
    when (target) {
        READY -> "Rechnung ausstellen"
        SENT -> "Als versendet markieren"
        PAID -> "Als bezahlt markieren"
        CANCELLED -> "Rechnung stornieren"
        else -> "Status ändern: ${invoiceStatusLabel(target)}"
    }

/** Cancelling is destructive; every other next state is the card's primary action. */
internal fun transitionVariant(target: String) =
    if (target == CANCELLED) ButtonVariant.DANGER else ButtonVariant.PRIMARY

private fun confirmLabel(target: String) =
    when (target) {
        READY -> "Ausstellung bestätigen"
        SENT -> "Versand bestätigen"
        PAID -> "Zahlung bestätigen"
        CANCELLED -> "Stornierung bestätigen"
        else -> "Bestätigen"
    }

private fun consequences(target: String, invoice: InvoiceResponse) =
    when (target) {
        READY ->
            "Beim Bestätigen wird eine eindeutige Rechnungsnummer vergeben und das Rechnungs-PDF " +
                "erstellt. Danach sind Empfänger, Daten, Positionen, Summen, Sprache und PDF der " +
                "Rechnung unveränderlich; Korrekturen sind nur durch Stornierung möglich."
        SENT ->
            "Bestätige, dass die Rechnung ${invoice.number.orEmpty()} ausserhalb von Jessie " +
                "versendet wurde. Das Versanddatum wird erfasst; die Rechnungsangaben bleiben " +
                "unverändert."
        PAID ->
            "Erfasse den Zahlungseingang für die Rechnung ${invoice.number.orEmpty()}. Das " +
                "Zahlungsdatum bestimmt die Periode der Ist-Versteuerung."
        CANCELLED ->
            "Die Stornierung ist endgültig. Rechnungsnummer, Rechnungsangaben und Statusverlauf " +
                "bleiben erhalten; importierte Arbeit wird für eine neue Rechnung freigegeben."
        else -> "Bitte bestätige den Statuswechsel."
    }

private const val CONFIRMATION_ID = "invoice-transition-confirmation"
private const val CONFIRMATION_TITLE_ID = "invoice-transition-title"
private const val CONFIRMATION_TEXT_ID = "invoice-transition-consequences"
private const val MAX_REASON = 1000
